import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useCart } from '@/context/CartContext';
import ProductModal from '@/components/ProductModal';
import { ShoppingBag } from 'lucide-react';

function ProductCard({ product, index, settings, onSelect }) {
  const { addItem } = useCart();
  const mainVariant = product.variants?.[0];
  const gallery = product.gallery || [];
  const coverImg = gallery.find(g => g.is_cover) || gallery[0];
  const image = coverImg?.md || coverImg?.lg || product.thumbnail_url;
  const prices = product.variants?.map(v => parseFloat(v.retail_price)).filter(Boolean) || [];
  const minPrice = prices.length ? Math.min(...prices) : 0;
  const maxPrice = prices.length ? Math.max(...prices) : 0;
  const priceDisplay = minPrice === maxPrice ? `$${minPrice.toFixed(2)}` : `$${minPrice.toFixed(2)} – $${maxPrice.toFixed(2)}`;

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    if (!mainVariant) return;
    if (product.variants?.length > 1) { onSelect(product); return; }
    addItem({
      variant_id: mainVariant.variant_id,
      product_id: product.id,
      name: product.name,
      variant_name: mainVariant.name,
      price: mainVariant.retail_price,
      currency: mainVariant.currency || 'USD',
      image_url: image,
      quantity: 1,
    });
  };

  return (
    <div
      data-testid={`product-card-${index}`}
      className="group cursor-pointer"
      onClick={() => onSelect(product)}
    >
      <div className="overflow-hidden relative" style={{ aspectRatio: '3/4', border: '1px solid var(--border-color)' }}>
        {image ? (
          <img
            src={image}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
            style={{ transition: 'transform 0.5s ease' }}
            onMouseEnter={e => e.target.style.transform = 'scale(1.04)'}
            onMouseLeave={e => e.target.style.transform = 'scale(1)'}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--card-bg, var(--bg-color))' }}>
            <ShoppingBag size={32} style={{ opacity: 0.2 }} />
          </div>
        )}
        {/* Quick add overlay */}
        <button
          data-testid={`quick-add-${index}`}
          onClick={handleQuickAdd}
          className="absolute bottom-0 left-0 right-0 py-3 text-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            backgroundColor: 'var(--accent-color)',
            color: '#fff',
            fontFamily: 'var(--body-font)',
            fontSize: '0.6rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          {product.variants?.length > 1 ? 'View Options' : 'Add to Cart'}
        </button>
      </div>
      <div className="mt-3">
        <span style={{
          fontFamily: 'var(--body-font)',
          fontSize: '0.55rem',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: 'var(--muted-color)',
        }}>
          Page {String(index + 1).padStart(2, '0')}
        </span>
        <h3 className="mt-1" style={{
          fontFamily: 'var(--headline-font)',
          fontStyle: settings?.headline_italic !== false ? 'italic' : 'normal',
          fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)',
          textTransform: settings?.headline_uppercase !== false ? 'uppercase' : 'none',
          lineHeight: 1.2,
        }}>
          {product.name}
        </h3>
        <p className="mt-1" style={{ fontFamily: 'var(--body-font)', fontSize: '0.85rem', fontWeight: 600 }}>
          {priceDisplay}
        </p>
      </div>
    </div>
  );
}

export default function StoreSection({ content, adminPreview }) {
  const store = content.store || {};
  const settings = content.settings || {};
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const storeVisible = store.open || adminPreview;

  useEffect(() => {
    if (storeVisible) {
      setLoading(true);
      const url = adminPreview ? '/shop/products?admin_preview=1' : '/shop/products';
      api.get(url).then(({ data }) => {
        setProducts(data.products || []);
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, [storeVisible, adminPreview]);

  const showTeaser = !storeVisible || (storeVisible && products.length === 0 && !loading);

  // Teaser view (Coming Soon)
  if (showTeaser) {
    return (
      <section id="store" data-testid="store-section-teaser" className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="flex flex-col items-center justify-center text-center" style={{ minHeight: '40vh' }}>
          <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--muted-color)', marginBottom: '1.5rem' }}>
            {store.label}
          </span>
          <h2 style={{
            fontFamily: 'var(--headline-font)',
            fontStyle: settings.headline_italic !== false ? 'italic' : 'normal',
            fontSize: 'clamp(2.5rem, 7vw, 5rem)',
            textTransform: settings.headline_uppercase !== false ? 'uppercase' : 'none',
            letterSpacing: '-0.02em',
            lineHeight: 0.95,
            marginBottom: '1.5rem',
            whiteSpace: 'pre-line',
          }}>
            {store.teaser_title}
          </h2>
          <div className="w-12 my-6" style={{ borderTop: '2px solid var(--accent-color)' }} />
          <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.85rem', lineHeight: 1.7, color: 'var(--muted-color)', maxWidth: '28rem' }}>
            {store.teaser_description}
          </p>
          <span className="mt-8 inline-block px-6 py-2.5" style={{ border: '1px solid var(--border-color)', fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>
            {store.teaser_badge}
          </span>
        </div>
      </section>
    );
  }

  // Store view
  return (
    <section id="store" data-testid="store-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20" style={{ borderBottom: '1px solid var(--border-color)' }}>
      {/* Section header */}
      <div className="flex justify-between items-baseline pb-3 mb-10" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div>
          <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)', display: 'block', marginBottom: '0.5rem' }}>
            {store.label}
          </span>
          <h2 style={{
            fontFamily: 'var(--headline-font)',
            fontStyle: settings.headline_italic !== false ? 'italic' : 'normal',
            fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
            textTransform: settings.headline_uppercase !== false ? 'uppercase' : 'none',
            letterSpacing: '-0.01em',
          }}>
            {store.title}
          </h2>
        </div>
        <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>
          {products.length} {products.length === 1 ? 'Item' : 'Items'}
        </span>
      </div>

      {store.subtitle && (
        <p className="mb-10" style={{ fontFamily: 'var(--body-font)', fontSize: '0.9rem', color: 'var(--muted-color)', maxWidth: '32rem' }}>
          {store.subtitle}
        </p>
      )}

      {loading ? (
        <div className="text-center py-16">
          <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>Loading...</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-10">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} settings={settings} onSelect={setSelectedProduct} />
          ))}
        </div>
      )}

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          settings={settings}
        />
      )}
    </section>
  );
}
